// GA4 ecommerce dataLayer push helper.
//
// This is the single source of truth for the GA4 dataLayer push contract.
// All GA4 ecommerce events pushed from the paywall checkout flow use these
// functions so the payload shape is consistent across call sites.
//
// Background: GTM is already injected for non-Circle/non-masquerade users in
// production (`shared/header/_code_snippets.html.erb`), so `window.dataLayer`
// is available in those sessions. For Circle staff and masquerade sessions it
// is absent — the optional-chain guards below make every push a no-op in that
// case. Do NOT add `window?.` before `window` — `window` is always defined in
// browser context; only `dataLayer` may be absent. Note: the onboarding idiom
// (`useOnboardingSubmission.ts`) writes `window?.dataLayer?.push?.(...)` — the
// redundant `window?.` is intentionally dropped here per the spec.

import { getIntervalType } from "@circle-react/helpers/paywallPriceHelpers";
import type { PaywallCoupon } from "@circle-react/types/PaywallCoupon";
import { paywallPriceTypes } from "@circle-react/types/PaywallPrice";
import type {
  Ga4Ecommerce,
  Ga4Item,
  Ga4Paywall,
  Ga4PaywallPrice,
  Ga4PurchaseEcommerce,
  Ga4Redemption,
  Ga4Step,
} from "@circle-react/types/Paywalls/Ga4DataLayer";

// ---------------------------------------------------------------------------
// Helper: resolve a coupon redemption from a step purchase record.
//
// Returns a Ga4Redemption when both paywall_coupon (code) and the first
// coupon_application (discount_amount) are present; otherwise undefined.
// Both keys are omitted entirely from GA4 payloads when undefined — never
// sent as null (Decision 6 in the spec).
// ---------------------------------------------------------------------------
const resolveRedemption = (record: {
  paywall_coupon?: Pick<PaywallCoupon, "code"> | null;
  coupon_applications?: {
    applications?: Array<{
      discount_amount: number;
      discount_amount_decimal: number;
    }>;
  } | null;
}): Ga4Redemption | undefined => {
  const code = record.paywall_coupon?.code;
  const application = record.coupon_applications?.applications?.[0];

  if (!code || application === undefined) {
    return undefined;
  }

  return {
    code,
    discount_amount: application.discount_amount,
    discount_amount_decimal: application.discount_amount_decimal,
  };
};

// ---------------------------------------------------------------------------
// Helper: resolve the billing interval label for item_variant.
//
// Delegates to getIntervalType (from paywallPriceHelpers) which maps
// interval + interval_count → frequency label (e.g. "monthly", "quarterly").
// The one exception: getIntervalType returns "onetime" for one-time prices,
// but GA4 expects "one_time" (with underscore) — so we map that case.
// Free-trial subscriptions use the billing interval label — never "trial"
// (spec Decision per interval).
// ---------------------------------------------------------------------------
const resolveItemVariant = (price: Ga4PaywallPrice): string => {
  const intervalType = getIntervalType(price);
  if (intervalType === paywallPriceTypes.onetime) {
    return "one_time";
  }
  return intervalType;
};

// ---------------------------------------------------------------------------
// Core functions
// ---------------------------------------------------------------------------

// pushGa4Event — the only function that writes to window.dataLayer.
//
// Clears the prior ecommerce object first (GA4 best practice to prevent
// pollution across events), then pushes the new event.
//
// Uses optional-chain guards on `dataLayer` and `push`. `window` itself is
// not optional-chained because it is always defined in browser context.
const pushGa4Event = (event: string, ecommerce: Ga4Ecommerce): void => {
  window.dataLayer?.push?.({ ecommerce: null }); // clear prior ecommerce object
  window.dataLayer?.push?.({ event, ecommerce });
};

// pushPurchaseEvent — public entry point for emitting a GA4 purchase event.
//
// Wraps pushGa4Event with the canonical "purchase" event name. Accepts a
// Ga4PurchaseEcommerce so the compiler enforces transaction_id/value/currency
// at every call site (GA4 silently drops purchases missing these fields).
export const pushPurchaseEvent = (ecommerce: Ga4PurchaseEcommerce): void =>
  pushGa4Event("purchase", ecommerce);

// buildItem — constructs a single GA4 ecommerce item from a paywall, its
// price option, and an optional coupon redemption.
//
// GA4 items[].price is the *list* price; ecommerce.value is the *charged*
// amount. For free trials, ecommerce.value is 0 but items[].price is the
// plan price — GA4 uses the difference to compute discount in reports.
//
// `redemption` is omitted entirely (not null) when no coupon was applied
// (Decision 6). Do not pass `null` — pass `undefined` or omit the argument.
export const buildItem = (
  paywall: Ga4Paywall,
  price: Ga4PaywallPrice,
  redemption?: Ga4Redemption,
): Ga4Item => {
  const item: Ga4Item = {
    item_id: String(paywall.id),
    item_name: paywall.display_name ?? paywall.name ?? "",
    item_variant: resolveItemVariant(price),
    price: price.amount_decimal,
    quantity: 1,
  };

  if (redemption) {
    item.coupon = redemption.code;
    item.discount = redemption.discount_amount_decimal;
  }

  return item;
};

// buildPurchaseEcommerce — constructs a complete Ga4PurchaseEcommerce from a
// completed session step, or returns undefined when required GA4 fields cannot
// be resolved. Callers must skip the push when undefined is returned.
//
// Subscription vs charge discrimination: `community_member_subscription` is
// null for charge-only (one-time) steps and non-null for subscription/
// installment steps. `subscription != null` is the direct signal — no
// reliance on serializer SKIP behavior.
//
// Amount conversion: amounts are provided by the backend in major units via
// `amount_decimal` (using Currency#amount) — no frontend division needed.
// This is correct for zero-decimal currencies (JPY, KRW, etc.) as well as
// standard decimal currencies.
//
// transaction_id derivation:
//   - Subscription/installment step: subscription.processor_id (sub_xxx).
//     Matches the onComplete path in ga4PurchaseHelper and is the canonical
//     GA4 identifier for recurring purchases.
//   - Charge-only (one-time) step: charge.processor_id ?? charge.invoice_processor_id.
//
// Value derivation: `value` is always derived from `charge.amount_decimal`:
//   - Free trial: charge may be null → value defaults to 0 ✓
//   - Paid subscription: charge has the billed amount → value > 0 ✓
//   - One-time: charge exists → value ✓
//   - Installment: charge has the upfront deposit → value ✓
// The subscription serializer no longer exposes a redundant amount field
// because `charge` is already a sibling on the step.
//
// Null charge: `community_member_charge` is null for free-trial subscription
// steps (no charge issued). For these steps, transaction_id comes from
// subscription.processor_id (safe) and value defaults to 0 via optional-chaining.
// For charge-only steps with a null charge (unexpected), transactionId resolves
// to undefined and the missing-fields guard returns undefined.
export const buildPurchaseEcommerce = (
  step: Ga4Step,
): Ga4PurchaseEcommerce | undefined => {
  const subscription = step.community_member_subscription;
  const charge = step.community_member_charge;

  // subscription is null for charge-only (one-time) steps; non-null for
  // subscription and installment steps (mirrors Ruby's purchase_record rule:
  // subscription || charge).
  const isSubscriptionStep = subscription != null;

  // Resolve transaction_id and value.
  //
  // transaction_id:
  //   - Subscription/installment step: subscription.processor_id (the sub_xxx).
  //     This matches the onComplete path in ga4PurchaseHelper.ts and is the
  //     canonical GA4 identifier for recurring purchases.
  //   - Charge-only (one-time) step: charge.processor_id ?? charge.invoice_processor_id.
  //
  // value:
  //   Always derived from charge.amount_decimal. Free trials produce a $0 charge
  //   so this correctly yields 0. Charge may be null for free-trial subscription
  //   steps — the optional-chain guard handles that case.
  const transactionId: string | undefined = isSubscriptionStep
    ? subscription.processor_id
    : (charge?.processor_id ?? charge?.invoice_processor_id);

  // value is always derived from charge.amount_decimal. Free trials produce a
  // $0 charge, so this correctly yields 0 for free-trial subscriptions.
  const value: number = charge?.amount_decimal ?? 0;

  // Currency comes from the paywall_price nested in whichever record is active.
  // For installment steps both a subscription and a charge exist; subscription
  // takes precedence (per the Ruby purchase_record rule), so the subscription
  // paywall_price — which reflects the installment cadence — is the correct
  // source for item_variant and price.
  // For charge-only steps, charge may be null (unexpected but guarded) —
  // priceRecord will be undefined and the missing-fields guard below will catch it.
  const priceRecord = isSubscriptionStep
    ? subscription.paywall_price
    : charge?.paywall_price;

  const currencyCode: string | undefined = priceRecord?.currency?.code;

  // Guard: skip the push entirely when required GA4 fields cannot be resolved.
  // An empty transaction_id or currency causes GA4 to silently drop the event
  // and would pollute the customer's property with malformed conversions.
  if (!transactionId || !currencyCode || !priceRecord) {
    console.error(
      "[ga4DataLayer] buildPurchaseEcommerce: required fields missing — skipping push.",
      {
        step,
        isSubscriptionStep,
        transactionId,
        value,
        currencyCode,
      },
    );
    return undefined;
  }

  // Resolve coupon redemption from the active purchase record.
  // For charge-only steps, charge is guaranteed non-null here — the missing-fields
  // guard above returns undefined when charge is null (transactionId resolves to
  // undefined via charge?.processor_id and the !transactionId guard fires).
  const couponRecord = isSubscriptionStep
    ? {
        paywall_coupon: subscription.paywall_coupon,
        coupon_applications: subscription.coupon_applications,
      }
    : {
        paywall_coupon: charge?.paywall_coupon,
        coupon_applications: charge?.coupon_applications,
      };

  const redemption = resolveRedemption(couponRecord);

  // Resolve the paywall from the main offer.
  const mainOffer = step.offers.find(o => o.main);
  if (!mainOffer?.paywall) {
    console.error(
      "[ga4DataLayer] buildPurchaseEcommerce: no main offer paywall found — skipping push.",
      { step },
    );
    return undefined;
  }

  const ecommerce: Ga4PurchaseEcommerce = {
    currency: currencyCode,
    items: [buildItem(mainOffer.paywall, priceRecord, redemption)],
    transaction_id: transactionId,
    value,
  };

  if (redemption) {
    ecommerce.coupon = redemption.code;
    ecommerce.discount = redemption.discount_amount_decimal;
  }

  return ecommerce;
};
